import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, catchError, filter, map, Observable, of, pairwise, startWith, Subscriber, switchMap} from "rxjs";
import {firebaseDb} from "../common/service/firebase";
import {onValue, ref} from "firebase/database";
import {Round, RoundInfo, RoundSettings, Scorecard} from "./model/round";


@Injectable({
    providedIn: 'root'
})
export class RoundService {

    constructor(private httpClient: HttpClient) {

    }

    getRound(token: string): Observable<Round | null> {
        let reloadRequest = new BehaviorSubject(null);

        return reloadRequest.pipe(
            switchMap(() => {
                return this.doGetRound(token, () => {
                    reloadRequest.next(null)
                })
            })
        )
    }

    private doGetRound(token: string, reload: () => void): Observable<Round | null> {
        return this.httpClient.get<GetRoundResponse>("/rest/round/" + token).pipe(
            map(response => response.round),
            switchMap(round => {
                if (round.type == "LiveRound") {
                    return this.getLiveRound(token);
                } else {
                    return this.getRegularRound(round as RegularRound)
                }
            }),
            startWith(null),
            pairwise(),
            map(([previousValue, currentValue]) => {
                if (previousValue != null && currentValue == null) {
                    reload()
                }
                return currentValue
            }),
            filter(round => round != null),
            catchError(() => of(null))
        )
    }

    private getLiveRound(token: string) {
        const reference = ref(firebaseDb, token);
        return new Observable<Round | null>((subscriber: Subscriber<Round | null>) => {
                return onValue(reference, (snapshot) => {
                    let value = snapshot.val()
                    subscriber.next(value ? toRound(value) : null)
                });
            }
        )
    }

    private getRegularRound(round: RegularRound): Observable<Round> {
        return of(round);
    }
}

function toRound(value: LiveRound): Round {
    return {
        info: undefined,
        settings: value.settings,
        scorecard: value.scorecard
    }
}

interface LiveRound {
    protocolVersion: number,
    settings: RoundSettings,
    scorecard: Scorecard
}

interface GetRoundResponse {
    round: ResponseRound
}

interface ResponseRound {
    type: RoundType;
}

enum RoundType {
    LiveRound = "LiveRound",
    RegularRound = "RegularRound"
}

interface RegularRound extends ResponseRound {
    info: RoundInfo,
    settings: RoundSettings,
    scorecard: Scorecard
}
